import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { CampaignHealthPayload } from "@/types/marketing";

type GaugeProps = {
  value: number;
  label: string;
  color: string;
};

function CircularGauge({ value, label, color }: GaugeProps) {
  const clamped = Math.max(0, Math.min(100, value || 0));
  const rotation = (clamped / 100) * 360;
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative flex h-28 w-28 items-center justify-center rounded-full bg-background/40 text-foreground"
        style={{
          background: `conic-gradient(${color} ${rotation}deg, #1e293b ${rotation}deg)`
        }}
      >
        <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full bg-background">
          <span className="text-lg font-semibold text-foreground">{clamped.toFixed(0)}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Score</span>
        </div>
      </div>
      <p className="text-sm font-medium text-foreground/80 text-center">{label}</p>
    </div>
  );
}

type CampaignHealthPanelProps = {
  data: CampaignHealthPayload | null;
  loading?: boolean;
};

const metricConfig = [
  { key: "postingConsistency", label: "Posting Consistency", color: "#34d399" },
  { key: "engagementTrend", label: "Engagement Trend", color: "#38bdf8" },
  { key: "pressCoverage", label: "Press Coverage", color: "#fbbf24" },
  { key: "assetDiversity", label: "Asset Diversity", color: "#a855f7" },
  { key: "ctaPerformance", label: "CTA Performance", color: "#f97316" },
] as const;

export function CampaignHealthPanel({ data, loading }: CampaignHealthPanelProps) {
  const scores = data?.scores ?? {
    postingConsistency: 0,
    engagementTrend: 0,
    pressCoverage: 0,
    assetDiversity: 0,
    ctaPerformance: 0,
  };
  const warnings = data?.warnings ?? [];
  const suggestions = data?.suggestions ?? [];

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Campaign Health</p>
        <p className="text-xs text-muted-foreground">Heuristics across cadence, press, assets, and conversion.</p>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Evaluating campaign health…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {metricConfig.map((metric) => (
              <CircularGauge key={metric.key} value={scores[metric.key]} label={metric.label} color={metric.color} />
            ))}
          </div>

          {warnings.length ? (
            <div className="rounded-xl border border-border/70 bg-background/50 p-3">
              <p className="text-xs uppercase tracking-wide text-warning">Warnings</p>
              <div className="mt-2 space-y-2">
                {warnings.map((warning) => (
                  <div key={warning} className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {suggestions.length ? (
            <div className="rounded-xl border border-border/70 bg-background/50 p-3">
              <p className="text-xs uppercase tracking-wide text-success">Suggestions</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {suggestions.map((suggestion) => (
                  <Badge key={suggestion} variant="outline" className="border-success/30 text-success">
                    {suggestion}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}
